import Cookies from 'js-cookie';
import type { Request } from 'Common/types/express';
import {
  privacyControl,
  type PrivacyControlReturnType
} from '@biome-design-system/privacy-control';
import { iabTcfChecker } from '@biome-design-system/privacy-control/checkers/iabTcf';
import { oneTrustChecker } from '@biome-design-system/privacy-control/checkers/oneTrust';
import type { PMCConfig } from '@kyruus/types';

import { getConfigProperty } from 'Common/config/utils';

type BaseCookieParams = {
  httpOnly: boolean;
  secure: boolean;
  sameSite: 'None' | 'Strict' | 'Lax';
  partitioned: boolean;
  path: string;
};

const BASE_COOKIE_PARAMS: BaseCookieParams = {
  httpOnly: false,
  secure: true,
  sameSite: 'None',
  partitioned: true,
  path: '/'
};

export function getBaseCookieParams(): BaseCookieParams {
  return BASE_COOKIE_PARAMS;
}

/**
 * Get cookies from the request headers on the server side, or from document.cookie on the client side
 *
 * @param req
 * @returns
 */
function getCookies(req?: Request) {
  if (req) {
    return req.headers.cookie ?? {};
  }
  if (typeof window !== 'undefined') {
    return Cookies.get();
  }
  return {};
}

/**
 * Get tracking privacy preference based on the use_privacy_control feature flag and the privacy control library if enabled
 * @param {PMCConfig} config - The PMC configuration object
 * @param {Request} [req] - The Express request object, required if running on the server side to access cookies
 * @returns {PrivacyControlReturnType} The tracking privacy preference
 */
export function getTrackingPrivacyPreference({
  config,
  req
}: {
  config: PMCConfig;
  req?: Request;
}): PrivacyControlReturnType {
  const usePrivacyControl = getConfigProperty(
    config,
    'feature_flags.use_privacy_control',
    false
  );

  if (!usePrivacyControl) {
    return {
      consent: true,
      type: 'none'
    };
  }

  const privacyControlSettings = privacyControl({
    checkers: [iabTcfChecker, oneTrustChecker],
    cookies: getCookies(req),
    navigator: req ? undefined : window.navigator
  });
  return privacyControlSettings;
}
